/**
 * Dock Strip Rect Builder
 * One-shot DOM snapshot of the currently docked entries (sidepanels +
 * toolbar rail) in per-edge strip order, consumed by dockSnapResolver while
 * a drag is live. Elements announce themselves with data attributes:
 *
 *   data-dock-entry="main-panel"     — any docked entry root
 *   data-dock-side="left|right|top|bottom"
 *   data-dock-index="2"              — position inside that side's strip
 *
 * Collecting from the DOM (instead of React state) keeps the resolver working
 * for every mounted surface — App-owned ToolBar, SidePanel instances and
 * dynamic panels alike — without prop threading.
 */

import { DockEntryRect } from './dockSnapResolver';
import { DockSide } from '../store/useDockLayoutStore';

const SIDES: DockSide[] = ['left', 'right', 'top', 'bottom'];

/** Flatten the live DOM into per-edge rect lists, each side ordered by its
    data-dock-index attribute (strip = outermost first), with a geometric
    fallback when an index is missing. */
export function collectDockEntryRects(): {
    rects: DockEntryRect[];
    sides: Partial<Record<DockSide, DockEntryRect[]>>;
} {
    const elements = Array.from(
        document.querySelectorAll<HTMLElement>('[data-dock-entry]'),
    );
    const sides: Partial<Record<DockSide, DockEntryRect[]>> = {};

    for (const el of elements) {
        const side = el.dataset.dockSide as DockSide | undefined;
        if (!side || !SIDES.includes(side)) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue;
        const entry: DockEntryRect = {
            id: el.dataset.dockEntry || '',
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
        };
        (sides[side] ||= []).push(entry);
    }

    for (const side of SIDES) {
        sides[side]?.sort((a, b) => {
            const indexOf = (id: string) => {
                if (!id) return Number.NaN;
                const el = document.querySelector<HTMLElement>(
                    `[data-dock-entry="${CSS.escape(id)}"]`,
                );
                const n = Number(el?.dataset.dockIndex);
                return Number.isFinite(n) ? n : Number.NaN;
            };
            const ia = indexOf(a.id);
            const ib = indexOf(b.id);
            if (!Number.isNaN(ia) && !Number.isNaN(ib) && ia !== ib) {
                return ia - ib;
            }
            // Geometric fallback: outermost-first per edge
            if (side === 'left' || side === 'right') return a.top - b.top;
            if (side === 'top') return a.left - b.left;
            return b.left - a.left;
        });
    }

    const rects = SIDES.flatMap((side) => sides[side] ?? []);
    return { rects, sides };
}
